import { silentWavBlob } from '@/utils/silent-wav'

/**
 * Puente con el sistema operativo para el reproductor de audiolibro:
 * MediaSession (metadatos + controles de auriculares/pantalla de bloqueo) y
 * Screen Wake Lock (que la pantalla no se apague).
 *
 * Todo con feature-detect. Safari no implementa Wake Lock y jsdom no
 * implementa ninguna de las dos: la ausencia degrada, nunca lanza.
 *
 * `silentKeepAlive` es para el motor navegador: `speechSynthesis` no crea
 * sesión de medios, así que reproducimos un WAV silencioso en bucle solo
 * para adquirirla, y pedimos el wake lock porque la síntesis del navegador
 * se suspende al apagarse la pantalla.
 *
 * ponytail: el wake lock mantiene la pantalla encendida y gasta batería. Es
 * el techo asumido del motor gratis; el motor neural no lo necesita porque
 * reproduce audio real. Si algún día hay TTS neural local, este truco muere.
 */

export interface NowPlayingMeta {
  title: string
  author: string
  /** URL (objectURL) de la portada, o null si no hay. */
  artworkUrl: string | null
}

export interface NowPlayingHandlers {
  play(): void
  pause(): void
  next(): void
  prev(): void
}

/**
 * Salto de ±N segundos desde los controles del sistema. `offset` es el que
 * pida la propia acción (`seekOffset`), que no siempre viene: algunos mandos
 * imponen su propio salto y otros lo dejan a la app. Quien los registra
 * decide qué usar cuando no viene.
 *
 * Van aparte de `NowPlayingHandlers` porque no todos los motores pueden
 * saltar: el del navegador no tiene posición que mover, y ahí estos handlers
 * se RETIRAN (ver `setSeekHandlers`) en vez de quedarse puestos sin hacer
 * nada.
 */
export interface NowPlayingSeekHandlers {
  backward(offset?: number): void
  forward(offset?: number): void
}

/** Subconjunto de la API de Wake Lock que usamos (no está en lib.dom aún). */
interface WakeLockSentinelLike {
  release(): Promise<void>
}
interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>
}

function mediaSession(): MediaSession | null {
  return 'mediaSession' in navigator ? navigator.mediaSession : null
}

function wakeLockApi(): WakeLockLike | null {
  const nav = navigator as Navigator & { wakeLock?: WakeLockLike }
  return nav.wakeLock ?? null
}

export function useNowPlaying() {
  let keepAlive: HTMLAudioElement | null = null
  let sentinel: WakeLockSentinelLike | null = null

  function setMetadata(meta: NowPlayingMeta) {
    const ms = mediaSession()
    if (!ms || typeof MediaMetadata === 'undefined') return
    ms.metadata = new MediaMetadata({
      title: meta.title,
      artist: meta.author,
      artwork: meta.artworkUrl ? [{ src: meta.artworkUrl }] : [],
    })
  }

  function setHandlers(h: NowPlayingHandlers) {
    const ms = mediaSession()
    if (!ms) return
    ms.setActionHandler('play', () => h.play())
    ms.setActionHandler('pause', () => h.pause())
    ms.setActionHandler('nexttrack', () => h.next())
    ms.setActionHandler('previoustrack', () => h.prev())
  }

  /**
   * Registra —o retira, con `null`— los saltos de ±15 s de la pantalla de
   * bloqueo y los auriculares.
   *
   * Va aparte de `setHandlers` porque, a diferencia de play/pausa/anterior/
   * siguiente, esto depende del motor activo y cambia en caliente: retirar el
   * handler es lo que hace que el sistema deje de pintar ese botón. Un
   * `seekbackward` registrado que no hace nada es un botón que miente, y en la
   * pantalla de bloqueo el usuario no tiene forma de saber por qué.
   */
  function setSeekHandlers(h: NowPlayingSeekHandlers | null) {
    const ms = mediaSession()
    if (!ms) return
    ms.setActionHandler('seekbackward', h ? (d) => h.backward(d.seekOffset) : null)
    ms.setActionHandler('seekforward', h ? (d) => h.forward(d.seekOffset) : null)
  }

  function setPlaybackState(state: 'playing' | 'paused' | 'none') {
    const ms = mediaSession()
    if (!ms) return
    ms.playbackState = state
  }

  /**
   * Secuencia de `acquire`. Pedir el wake lock es asíncrono y el sistema puede
   * tardar: con dos adquisiciones solapadas (pausa y play seguidos, un cambio
   * de motor encima de un play) la respuesta de la VIEJA llegaba después y
   * sobrescribía `sentinel`, dejando el centinela de la nueva sin nadie que lo
   * soltara — la pantalla no volvía a apagarse en toda la sesión. Lo
   * incrementan `acquire` y `releaseWakeLock`: quien llega tarde a su propia
   * petición se suelta él mismo en vez de instalarse.
   */
  let acquireGen = 0

  async function acquire(opts: { silentKeepAlive: boolean }) {
    if (!opts.silentKeepAlive) {
      // El motor neural reproduce audio real: ni necesita el WAV silencioso
      // para sostener la sesión de medios ni la pantalla encendida. Salir por
      // aquí sin tocar nada dejaba lo del motor navegador vivo al cambiar de
      // uno a otro: su wake lock manteniendo la pantalla encendida toda la
      // escucha —el caso «me duermo escuchando»— y su `<audio>` fantasma en
      // bucle. Se sueltan los dos.
      keepAlive?.pause()
      releaseWakeLock()
      return
    }
    // WAV silencioso en bucle: sin un <audio> sonando no hay MediaSession.
    // Todo el bloque va protegido: entornos sin `URL.createObjectURL` (o sin
    // `play()`, como jsdom) no deben impedir que la voz suene.
    try {
      if (!keepAlive) {
        keepAlive = new Audio(URL.createObjectURL(silentWavBlob(1)))
        keepAlive.loop = true
        keepAlive.volume = 0
      }
      await keepAlive.play()
    } catch {
      // Autoplay bloqueado o API ausente: los controles del sistema no
      // aparecerán, pero la voz sí suena. No es motivo para abortar.
    }
    const api = wakeLockApi()
    if (!api) return
    // Soltar el centinela anterior antes de pedir otro: `acquire()` se llama
    // en cada play/resume y en cada cambio de motor, y sobrescribir
    // `sentinel` sin liberarlo dejaba el lock del tramo previo huérfano para
    // toda la vida de la pestaña — la pantalla ya no se apagaba ni con el
    // temporizador de sueño, que es justo el caso "me duermo escuchando".
    // Se vuelve a pedir (en vez de reutilizar el que hay) porque el sistema
    // puede haberlo revocado por su cuenta al ocultarse la pestaña.
    releaseWakeLock()
    const gen = ++acquireGen
    try {
      const granted = await api.request('screen')
      if (gen !== acquireGen) {
        // Otra adquisición —o un `releaseWakeLock()`— mandó mientras el
        // sistema respondía. Este centinela llega tarde: instalarlo pisaría el
        // vigente, que se quedaría vivo y sin referencia. Se suelta el que
        // sobra, que es este.
        void Promise.resolve(granted.release()).catch(() => {})
        return
      }
      sentinel = granted
    } catch {
      // El sistema puede denegarlo (batería baja, pestaña oculta). Se sigue.
    }
  }

  /**
   * Suelta solo el wake lock, sin tocar el `<audio>` de keep-alive ni la
   * MediaSession. Se usa al pausar: la pantalla puede apagarse (no hay
   * nada que proteger mientras no se habla), pero si además pausáramos el
   * `<audio>` silencioso, el sistema operativo daría por terminada la sesión
   * de medios y retiraría los controles de la pantalla de bloqueo — el
   * usuario perdería el botón de "play" justo cuando más lo necesita.
   * `acquire()` vuelve a pedirlo al reanudar.
   */
  function releaseWakeLock() {
    // Cuenta como una operación más nueva: una adquisición en vuelo que
    // resuelva después de esto no debe instalar su centinela — soltar y que
    // acto seguido aparezca un lock nuevo es exactamente lo que dejaba la
    // pantalla encendida tras pausar.
    acquireGen++
    // `Promise.resolve(...)` en vez de encadenar `.catch` directo: soporta
    // implementaciones (o mocks) cuyo `release()` no devuelva una promesa.
    void Promise.resolve(sentinel?.release()).catch(() => {})
    sentinel = null
  }

  function release() {
    keepAlive?.pause()
    releaseWakeLock()
    setPlaybackState('none')
  }

  return {
    setMetadata,
    setHandlers,
    setSeekHandlers,
    setPlaybackState,
    acquire,
    releaseWakeLock,
    release,
  }
}
